import React, { useEffect, useRef, useState } from "react"
import { range, includes } from "lodash"
import ClassNames from "classnames"
import dayjs from "dayjs"
import "dayjs/locale/ja"

import { TIME_TABLE_INFO } from "config/const"
import { ToolTip } from "components/atoms/text/tooltip"

type TimeTableProps = {
  thParts: HTMLTableHeaderCellElement
}

const TimeTableBar: React.FC<TimeTableProps> = props => {
  dayjs.locale("ja")
  const [nowTime, setTime] = useState<number>()
  const requestAnimation = useRef<number>()
  const openSecond = TIME_TABLE_INFO.hour[0] * 3600
  const closeSecond = TIME_TABLE_INFO.hour[1] * 3600
  const setTimeFunction = () => {
    const time =
      dayjs().get("h") * 3600 + dayjs().get("m") * 60 + dayjs().get("s")
    setTime(time)
    requestAnimation.current = requestAnimationFrame(setTimeFunction)
  }
  useEffect(() => {
    requestAnimation.current = requestAnimationFrame(setTimeFunction)
    return () => cancelAnimationFrame(requestAnimation.current)
  }, [])

  if (
    !props.thParts ||
    (!nowTime || (nowTime < openSecond || closeSecond < nowTime))
  ) {
    return null
  }

  const hourRange = TIME_TABLE_INFO.hour[1] - TIME_TABLE_INFO.hour[0]
  const timeWidth = props.thParts.offsetWidth * hourRange
  const timeWidthPerSecond = timeWidth / (closeSecond - openSecond)

  return (
    <div
      className="time-table-bar"
      style={{
        top: props.thParts.offsetHeight,
        left:
          props.thParts.offsetLeft +
          (nowTime - openSecond) * timeWidthPerSecond,
        height: `calc(100% - ${props.thParts.offsetHeight}px)`,
      }}
    />
  )
}

export const TimeTable = () => {
  const [timeTableParts, setTableParts] = useState<HTMLTableHeaderCellElement>()
  const [tooltipAttention, changeName] = useState<string>()
  const [tooltipPosition, setPosition] = useState<[number, number]>([0, 0])
  const hourRange = range(TIME_TABLE_INFO.hour[1] - TIME_TABLE_INFO.hour[0])
  const timeLabel = range(5)
  const separateNum = 12
  const timeSeparate = range(separateNum)
  const checkTime = (date: number, data: Array<Array<number>> | boolean) => {
    if (typeof data === "boolean") {
      return false
    } else {
      const check = data.map(row => row[0] <= date && date < row[1])
      return includes(check, true)
    }
  }

  const setTooltipPosition = (
    e: React.MouseEvent<HTMLElement>,
    title: string
  ) => {
    const clientRect = e.currentTarget.getBoundingClientRect()
    changeName(title)
    setPosition([
      clientRect.top + Number(e.currentTarget.clientHeight) + 10,
      clientRect.left + 30,
    ])
  }

  return (
    <div className="time-table-container">
      <table className="table table-time">
        <thead>
          <tr>
            <th className="title-cell" />
            {hourRange.map((i, key) => (
              <th
                key={key}
                className="time-range-cell"
                ref={elm => {
                  if (!key) setTableParts(elm)
                }}
              >
                {i + TIME_TABLE_INFO.hour[0]}時
              </th>
            ))}
          </tr>
          <tr className="range-row">
            <th className="title-cell" />
            {hourRange.map((hour, i) => (
              <th key={i} className="time-range-cell">
                <div className="time-range-row">
                  {timeLabel.map((time, j) => (
                    <div key={j}>{time === 2 ? `${time + 1}0` : null}</div>
                  ))}
                </div>
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {TIME_TABLE_INFO.market.map((data, i) => (
            <tr key={i}>
              <th className="title-cell">
                <div className="index-name-row">
                  <i className={ClassNames("icon-solid", data.icon)} />
                  <div className="index-name ellipsis-text">{data.country}</div>
                  {!!data.attention ? (
                    <i
                      className="material-icons icon-solid"
                      onMouseEnter={e => {
                        setTooltipPosition(e, data.attention)
                      }}
                      onMouseMove={e => {
                        setTooltipPosition(e, data.attention)
                      }}
                      onMouseLeave={() => {
                        changeName("")
                        setPosition([0, 0])
                      }}
                    >
                      help
                    </i>
                  ) : null}
                </div>
              </th>
              {hourRange.map((h, j) => (
                <td key={j} className="time-range-cell">
                  <div className="time-range-row">
                    {timeSeparate.map((t, k) => {
                      const hour = h + TIME_TABLE_INFO.hour[0]
                      const min = (60 / separateNum) * t
                      const date = Number(
                        min < 10 ? `${hour}0${min}` : `${hour}${min}`
                      )
                      return (
                        <div key={k} className="time-range-scale">
                          <div
                            className={ClassNames("time-range", {
                              "open-time": checkTime(date, data.open),
                              "close-time": includes(data.close, date),
                              "session-time": checkTime(date, data.preOpen),
                            })}
                          />
                        </div>
                      )
                    })}
                  </div>
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
      <ToolTip name={tooltipAttention} position={tooltipPosition} />
      <TimeTableBar thParts={timeTableParts} />
    </div>
  )
}
